import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { submitTrialRequest, clearTrialMessages } from "../redux/reducers/trialSlice";
import logo from "../assets/logo.png";
import { FiCheck, FiArrowLeft } from "react-icons/fi";

const TrialForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, submitted, error } = useSelector((state) => state.trial);

  const [form, setForm] = useState({ name: "", email: "", organization: "", dob: "" });

  useEffect(() => {
    dispatch(clearTrialMessages());
  }, [dispatch]);

  useEffect(() => {
    if (submitted) {
      navigate(`/trial/success?email=${encodeURIComponent(form.email)}`);
    }
  }, [submitted, navigate, form.email]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(submitTrialRequest(form));
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Navbar */}
      <header className="bg-[#3d184e] h-14 flex items-center justify-between px-6">
        <Link to="/" className="flex items-center gap-2">
          <img src={logo} alt="Logo" className="h-7 w-auto" />
          <span className="text-white font-bold hidden sm:block">Revenue Pro Planner</span>
        </Link>
        <Link
          to="/"
          className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-medium px-3 py-1.5 rounded-full transition-colors"
        >
          <FiArrowLeft />
          Back to Home
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg">
          {/* Header */}
          <div className="text-center mb-8">
            <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
              7-Day Free Trial
            </span>
            <h1 className="text-3xl font-bold text-[#2a496e] mb-2">Start Your Free Trial</h1>
            <p className="text-gray-500 text-sm">
              Fill in your details and we'll send your login credentials instantly — no credit card required.
            </p>
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-2 gap-3 mb-8">
            {["Full access to all features", "No credit card needed", "Instant login credentials", "7 days completely free"].map((b) => (
              <div key={b} className="flex items-center gap-2 text-sm text-gray-600">
                <div className="bg-green-100 text-green-600 rounded-full p-0.5 flex-shrink-0">
                  <FiCheck className="text-xs" />
                </div>
                {b}
              </div>
            ))}
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 mb-5 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="Jane Smith"
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  placeholder="jane@myschool.com"
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">School / Organization Name</label>
                <input
                  type="text"
                  name="organization"
                  value={form.organization}
                  onChange={handleChange}
                  required
                  placeholder="Glamour Beauty Academy"
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Date of Birth</label>
                <input
                  type="date"
                  name="dob"
                  value={form.dob}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#C9A84C] hover:brightness-110 disabled:opacity-60 text-[#3d184e] font-bold py-3 rounded-xl text-sm transition-all mt-2"
              >
                {loading ? "Setting Up Your Account..." : "Request Free Trial →"}
              </button>
            </form>

            <p className="text-center text-gray-400 text-xs mt-4">
              Already have an account?{" "}
              <Link to="/login" className="text-[#2e97a9] hover:underline font-medium">Sign in here</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrialForm;
